import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BaseDialogProps, BaseDialog } from './shared/components/BaseDialog';

/************************************
 * Dialog Provider Setup
 *************************************/

type DialogState<T extends UnknownProps> = {
  key: string;
  open: boolean;
  Component: GenericComponentType<T>;
  props: BaseDialogProps & T;
};

type UnknownProps = {
  [key: string]: unknown;
};

type DialogStates = {
  [key: string]: DialogState<UnknownProps>;
};

type DialogContextType = {
  dialogStates: DialogStates;
  setDialogStates: (states: DialogStates) => void;
};

const DialogContext = createContext<DialogContextType>(undefined);

export const DialogProvider = (props: { children: ReactNode }) => {
  const [dialogStates, setDialogStates] = useState<DialogStates>(undefined);

  return (
    <DialogContext.Provider value={{ dialogStates, setDialogStates }}>
      <DialogContainer
        dialogStates={dialogStates}
        setDialogStates={setDialogStates}
      />
      {props.children}
    </DialogContext.Provider>
  );
};

let allDialogStates: DialogStates = {};
const updateDialogState = <T extends UnknownProps>(
  key: string,
  state: Partial<DialogState<T>>,
  setDialogStates: (dialogStates: DialogStates) => void
) => {
  const currentValue = allDialogStates[key];

  const newStates: DialogStates = {
    ...allDialogStates,
    [key]: {
      key: key,
      Component: state.Component
        ? (state.Component as GenericComponentType<UnknownProps>)
        : currentValue.Component,
      props: state.props ? state.props : currentValue.props,
      open: state.open !== undefined ? state.open : currentValue?.open || false,
    } as DialogState<UnknownProps>,
  };

  allDialogStates = newStates;
  setDialogStates(newStates);
};

const clearDialogState = (
  key: string,
  setDialogStates: (dialogStates: DialogStates) => void
) => {
  const newStates = { ...allDialogStates };
  delete newStates[key];
  allDialogStates = newStates;
  setDialogStates(newStates);
};

const getDialogState = (key: string) => {
  return allDialogStates[key];
};

const DialogContainer = ({
  dialogStates,
  setDialogStates,
}: DialogContextType) => {
  const setOpen = (key: string, open: boolean) => {
    updateDialogState(key, { open }, setDialogStates);
  };

  return (
    <>
      {Object.values(dialogStates || {}).map(
        ({ key, open, Component, props }) => {
          return (
            <div key={key}>
              <BaseDialog
                {...props}
                setOpen={(value: boolean) => setOpen(key, value)}
                open={open}
              >
                <Component {...props}></Component>
              </BaseDialog>
            </div>
          );
        }
      )}
    </>
  );
};

/************************************
 * Dialog Hook
 *************************************/

type GenericComponentType<T> = (props: T) => JSX.Element;

type DependencyList = readonly unknown[];

export const useDialog = <T,>(
  Component: GenericComponentType<T>,
  propsBuilderCallback: () => BaseDialogProps & T,
  propDependencies: DependencyList
) => {
  const { setDialogStates } = useContext(DialogContext);

  const KEY = useMemo(() => {
    const newKey = 'RANDOM_KEY_' + Math.random();
    return newKey;
  }, []);

  useEffect(() => {
    const props = propsBuilderCallback();
    updateDialogState(
      KEY,
      {
        Component: Component as GenericComponentType<UnknownProps>,
        props: props as BaseDialogProps & UnknownProps,
      },
      setDialogStates
    );
  }, [Component, ...propDependencies]);

  useEffect(() => {
    return () => {
      clearDialogState(KEY, setDialogStates);
    };
  }, []);

  const toggleOpenDialog = (open: boolean) => {
    updateDialogState(KEY, { open }, setDialogStates);
  };
  const openDialog = () => {
    toggleOpenDialog(true);
  };

  return { openDialog, toggleOpenDialog, isOpen: getDialogState(KEY)?.open };
};
